/**
 * PersonalisedTrustPackForm — small lead-capture form on /trust.
 *
 * Prospect enters work email + company, hits "Send me a signed copy"
 * → backend mints a tracking token, embeds it in the PDF metadata +
 * a faint footer on every page, streams the PDF back. We trigger the
 * browser download via a Blob URL.
 *
 * Self-serve flow (3b-i): no email confirmation step, no admin queue.
 * Soft rate-limited server-side (3 per email per minute).
 */
import React, { useState } from 'react';
import { Mail, Building2, Download, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import ExplainThis from '@/components/ExplainThis';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

export default function PersonalisedTrustPackForm({ isDark }) {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [busy, setBusy] = useState(false);
  const [issuedToken, setIssuedToken] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      toast.error('A work email address is required.');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/trust/pack/personalized`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: trimmed, company: company.trim() }),
      });
      if (r.status === 429) {
        toast.error('Too many requests for this email — try again in a minute.');
        return;
      }
      if (!r.ok) {
        const txt = await r.text();
        toast.error(`Could not generate (HTTP ${r.status}). ${txt.slice(0, 120)}`);
        return;
      }
      const token = r.headers.get('x-tracking-token') || 'TKN-?????';
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trust-pack-${token.toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setIssuedToken(token);
      toast.success('Your personalised Trust Pack is downloading.');
    } catch (err) {
      toast.error('Network error — please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`mt-6 rounded-xl border p-5 md:p-6 ${
        isDark
          ? 'border-white/10 bg-white/[0.03]'
          : 'border-slate-200 bg-white'
      }`}
      data-testid="trust-personalised-form"
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className="h-10 w-10 rounded-md flex items-center justify-center border shrink-0"
          style={{
            backgroundColor: 'rgba(174,82,234,0.10)',
            color: 'var(--ve-violet-bright)',
            borderColor: 'rgba(174,82,234,0.30)',
          }}
        >
          <Mail size={18} strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[9px] uppercase tracking-[0.28em] font-bold" style={{ color: '#D4AF37' }}>
            [PERSONALISED FOR YOU]
          </div>
          <div className="font-bold text-base md:text-lg mt-0.5">
            Get a signed copy stamped with your name.
          </div>
          <p className={`mt-1.5 text-[12.5px] leading-relaxed ${isDark ? 'text-white/65' : 'text-slate-600'}`}>
            Same 8-doc bundle, plus a recipient page + faint per-page
            watermark with your email and a unique tracking token. The
            token also lives invisibly in the PDF metadata so we can
            attribute any later leak back to the original recipient.
          </p>
        </div>
      </div>

      {issuedToken ? (
        <div
          className={`rounded-md border p-4 ${
            isDark ? 'border-emerald-500/30 bg-emerald-500/[0.06]' : 'border-emerald-500/40 bg-emerald-50'
          }`}
          data-testid="trust-personalised-issued"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700 dark:text-emerald-300">
            <Check size={11}/> Issued
          </div>
          <div className="mt-2 text-sm">
            Your copy is downloading. Tracking token<ExplainThis topic="tracking-token" />:{' '}
            <code className="font-mono text-[12px] px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/5">
              {issuedToken}
            </code>
          </div>
          <button
            type="button"
            onClick={() => { setIssuedToken(null); setEmail(''); setCompany(''); }}
            className="mt-3 text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--ve-violet-bright)] hover:underline"
            data-testid="trust-personalised-reset"
          >
            Issue another →
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
          <label className="block">
            <span className={`text-[10px] uppercase tracking-[0.22em] font-bold ${isDark ? 'text-white/55' : 'text-slate-500'}`}>
              Work email
            </span>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="trust-personalised-email"
              className={`mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:border-[var(--ve-violet-bright)] ${
                isDark
                  ? 'border-white/15 bg-white/[0.03] text-white placeholder:text-white/35'
                  : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400'
              }`}
            />
          </label>
          <label className="block">
            <span className={`text-[10px] uppercase tracking-[0.22em] font-bold ${isDark ? 'text-white/55' : 'text-slate-500'}`}>
              Company (optional)
            </span>
            <div className="relative mt-1">
              <Building2 size={13} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50"/>
              <input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Acme Industries"
                data-testid="trust-personalised-company"
                className={`w-full rounded-md border pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[var(--ve-violet-bright)] ${
                  isDark
                    ? 'border-white/15 bg-white/[0.03] text-white placeholder:text-white/35'
                    : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400'
                }`}
              />
            </div>
          </label>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={busy}
              data-testid="trust-personalised-submit"
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-2 text-[11px] uppercase tracking-[0.22em] font-bold rounded-lg bg-[var(--ve-violet-bright)] text-white hover:bg-[#7C2BAB] transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>}
              {busy ? 'Generating…' : 'Send me a copy'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
